import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  clearRecentSearches, loadRecentSearches, MIN_QUERY_LENGTH, saveRecentSearch, SearchGroup, SearchHit, SearchKind,
  SEARCH_KIND_LABEL, useGlobalSearch,
} from '../data/search';
import { photoFor } from '../utils/names';
import NavIcon, { NavIconName } from '../layout/NavIcon';
import { buttonReset, color, font, radius, shadow, space, TAP, weight } from '../theme';
import SearchField from './SearchField';
import Avatar from './Avatar';
import { Badge, StageBadge } from './Badge';
import { Card, Section } from './Card';
import Button from './Button';
import Chip, { ChipRow } from './Chip';
import Icon from './Icon';
import { EmptyState, ErrorState } from './States';
import { SkeletonList } from './Skeleton';
import { BriefcaseIcon } from './lobbyists/RegistryBits';

/* Global search over MKs, parties, committees, ministries, clients of lobbyists, bills,
   lobbyists and queries. A client result opens /search?client=… (pages/SearchPage).
   - variant "page": the search screen (/search). Field on top, results grouped below.
   - variant "dropdown": a field whose results open in a panel under it (the desktop sidebar).
   It makes no assumptions about where it sits: the field fills its container, and the
   dropdown panel is absolutely positioned under the field (anchored right, RTL), with its
   width and max height from props. The shell decides placement. */

export interface GlobalSearchProps {
  variant?: 'page' | 'dropdown';
  // Controlled query (the search page keeps it in the URL); internal state when omitted
  query?: string;
  onQueryChange?: (query: string) => void;
  // Page: show one kind in full ("show all"); internal state when omitted
  type?: SearchKind | null;
  onTypeChange?: (type: SearchKind | null) => void;
  // Page: an element before the field, e.g. a back button
  leading?: React.ReactNode;
  autoFocus?: boolean;
  // Dropdown panel size in px (no vh: the desktop sidebar is CSS-zoomed)
  panelWidth?: number;
  panelMaxHeight?: number;
  // Called after the user picks a result
  onNavigate?: () => void;
  placeholder?: string;
}

const FROM = 'חיפוש';
const PAGE_PREVIEW = 4;
const DROPDOWN_PREVIEW = 3;
const PAGE_SIZE = 30;
const EXAMPLES = ['ועדת הכספים', 'שר האוצר', 'חוק יסוד'];

const KIND_ICON: Record<SearchKind, NavIconName | null> = {
  member: null, party: null, committee: 'committees', ministry: 'government', client: null, bill: 'legislation', lobbyist: null, query: 'queries',
};

// Bold the first place the query appears as typed
const highlight = (text: string, raw: string): React.ReactNode => {
  const q = raw.trim();
  if (q.length < MIN_QUERY_LENGTH) return text;
  const i = text.indexOf(q);
  if (i < 0) return text;
  return <>{text.slice(0, i)}<strong style={{ fontWeight: weight.bold }}>{text.slice(i, i + q.length)}</strong>{text.slice(i + q.length)}</>;
};

const Leading = ({ hit, size }: { hit: SearchHit; size: number }) => {
  if (hit.kind === 'member' && hit.member) return <Avatar name={hit.title} src={photoFor(hit.member, size)} size={size} />;
  const box: React.CSSProperties = { width: size, height: size, flexShrink: 0, borderRadius: hit.kind === 'party' ? radius.md : '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  if (hit.kind === 'party') return <span style={{ ...box, background: hit.swatch || color.track }} />;
  const nav = KIND_ICON[hit.kind];
  return (
    <span style={{ ...box, background: color.surfaceMuted, color: color.textSecondary }}>
      {nav ? <NavIcon name={nav} size={Math.round(size * 0.5)} />
        : hit.kind === 'client' ? <BriefcaseIcon size={Math.round(size * 0.5)} />
          : <Icon name="user" size={Math.round(size * 0.5)} />}
    </span>
  );
};

interface HitRowProps {
  hit: SearchHit;
  query: string;
  compact: boolean;
  divider: boolean;
  // Dropdown: listbox option driven by the field's arrow keys
  optionId?: string;
  active?: boolean;
  onHover?: () => void;
  onPick: () => void;
}

const HitRow = ({ hit, query, compact, divider, optionId, active = false, onHover, onPick }: HitRowProps) => {
  const size = compact ? 32 : 40;
  return (
    <Link
      to={hit.to}
      state={{ from: FROM }}
      id={optionId}
      role={optionId ? 'option' : undefined}
      aria-selected={optionId ? active : undefined}
      tabIndex={optionId ? -1 : undefined}
      onClick={onPick}
      onMouseMove={onHover}
      style={{
        display: 'flex', alignItems: 'center', gap: compact ? space.sm + 2 : space.md,
        minHeight: compact ? 48 : 56, boxSizing: 'border-box',
        padding: compact ? `${space.xs + 2}px ${space.md}px` : `${space.sm + 2}px ${space.lg}px`,
        borderTop: divider ? `1px solid ${color.divider}` : 'none',
        color: color.text, textDecoration: 'none',
        background: active ? color.primarySoft : 'transparent',
      }}
    >
      <Leading hit={hit} size={size} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, minWidth: 0 }}>
          <span style={{
            fontSize: compact ? font.small : font.label, fontWeight: weight.medium, lineHeight: 1.35, minWidth: 0,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>{highlight(hit.title, query)}</span>
          {hit.badge && <Badge>{hit.badge}</Badge>}
        </span>
        {hit.subtitle && <span style={{ fontSize: font.caption, color: color.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hit.subtitle}</span>}
      </span>
      {hit.stage && <StageBadge stage={hit.stage} />}
    </Link>
  );
};

/* The flattened dropdown options, in display order, for the arrow keys */
type DropdownOption =
  | { key: string; type: 'hit'; hit: SearchHit }
  | { key: string; type: 'more'; kind: SearchKind; count: number }
  | { key: string; type: 'recent'; text: string };

const GlobalSearch = ({
  variant = 'page', query: controlledQuery, onQueryChange, type: controlledType, onTypeChange, leading, autoFocus,
  panelWidth = 360, panelMaxHeight = 480, onNavigate, placeholder = 'חיפוש חברי כנסת, חוקים, ועדות ועוד',
}: GlobalSearchProps) => {
  const navigate = useNavigate();
  const isPage = variant === 'page';
  const [ownQuery, setOwnQuery] = useState('');
  const [ownType, setOwnType] = useState<SearchKind | null>(null);
  const query = controlledQuery !== undefined ? controlledQuery : ownQuery;
  const setQuery = (q: string) => { if (onQueryChange) onQueryChange(q); if (controlledQuery === undefined) setOwnQuery(q); };
  const type = controlledType !== undefined ? controlledType : ownType;
  const setType = (t: SearchKind | null) => { if (onTypeChange) onTypeChange(t); if (controlledType === undefined) setOwnType(t); };

  const [touched, setTouched] = useState(isPage);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recent, setRecent] = useState<string[]>(() => loadRecentSearches());
  const [visible, setVisible] = useState(PAGE_SIZE);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const baseId = useId().replace(/:/g, '');
  const listId = `${baseId}-list`;

  const { groups, loading, billsPartial, error } = useGlobalSearch(query, touched);
  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH;
  const total = groups.reduce((s, g) => s + g.hits.length, 0);

  // A new query starts from the top of the results
  useEffect(() => { setActiveIndex(-1); setVisible(PAGE_SIZE); }, [query, type]);

  // Dropdown: a click outside closes the panel
  useEffect(() => {
    if (isPage || !open) return;
    const onDown = (e: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isPage, open]);

  const pick = () => {
    setRecent(saveRecentSearch(query));
    if (!isPage) { setOpen(false); setQuery(''); }
    if (onNavigate) onNavigate();
  };
  const go = (hit: SearchHit) => { pick(); navigate(hit.to, { state: { from: FROM } }); };
  const showAllUrl = (kind: SearchKind | null) => `/search?q=${encodeURIComponent(trimmed)}${kind ? `&type=${kind}` : ''}`;

  /* Dropdown options for the arrow keys */
  const options: DropdownOption[] = [];
  if (!isPage) {
    if (!trimmed) recent.forEach(text => options.push({ key: `r_${text}`, type: 'recent', text }));
    else groups.forEach(g => {
      g.hits.slice(0, DROPDOWN_PREVIEW).forEach(hit => options.push({ key: `${g.kind}_${hit.id}`, type: 'hit', hit }));
      if (g.hits.length > DROPDOWN_PREVIEW) options.push({ key: `more_${g.kind}`, type: 'more', kind: g.kind, count: g.hits.length });
    });
  }
  const optionId = (i: number) => `${baseId}-opt-${i}`;
  const activate = (opt: DropdownOption) => {
    if (opt.type === 'hit') go(opt.hit);
    else if (opt.type === 'more') { setRecent(saveRecentSearch(query)); setOpen(false); if (onNavigate) onNavigate(); navigate(showAllUrl(opt.kind)); }
    else { setQuery(opt.text); setOpen(true); inputRef.current?.focus(); }
  };

  // Keep the highlighted option in view
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = document.getElementById(`${baseId}-opt-${activeIndex}`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, baseId]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (!isPage && open) { setOpen(false); setActiveIndex(-1); e.preventDefault(); return; }
      if (query) { setQuery(''); e.preventDefault(); }
      return;
    }
    if (isPage) {
      // Enter opens the best match
      if (e.key === 'Enter' && groups.length > 0) { e.preventDefault(); go((type ? groups.find(g => g.kind === type) || groups[0] : groups[0]).hits[0]); }
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (options.length === 0) return;
      setActiveIndex(i => (e.key === 'ArrowDown' ? Math.min(options.length - 1, i + 1) : Math.max(-1, i - 1)));
      return;
    }
    if (e.key === 'Enter') {
      const opt = activeIndex >= 0 ? options[activeIndex] : options.find(o => o.type === 'hit');
      if (opt) { e.preventDefault(); activate(opt); }
    }
  };

  const field = (
    <SearchField
      value={query}
      onChange={v => { setQuery(v); setTouched(true); if (!isPage) setOpen(true); }}
      placeholder={placeholder}
      label="חיפוש באתר"
      variant={isPage ? 'raised' : 'flat'}
      autoFocus={autoFocus}
      inputRef={inputRef}
      inputProps={isPage
        ? { onKeyDown, onFocus: () => setTouched(true) }
        : {
          role: 'combobox',
          'aria-expanded': open,
          'aria-controls': listId,
          'aria-autocomplete': 'list',
          'aria-activedescendant': open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
          onKeyDown,
          onFocus: () => { setTouched(true); setOpen(true); },
        }}
    />
  );

  /* ── Dropdown ── */
  if (!isPage) {
    const showPanel = open && (trimmed.length > 0 || recent.length > 0);
    const indexOf: Record<string, number> = {};
    options.forEach((o, i) => { indexOf[o.key] = i; });
    const optionStyle = (i: number): React.CSSProperties => ({ background: activeIndex === i ? color.primarySoft : 'transparent' });
    const renderOption = (opt: DropdownOption) => {
      const i = indexOf[opt.key];
      if (opt.type === 'hit') {
        return <HitRow key={opt.key} hit={opt.hit} query={query} compact divider={false} optionId={optionId(i)} active={activeIndex === i} onHover={() => setActiveIndex(i)} onPick={pick} />;
      }
      if (opt.type === 'more') {
        return (
          <Link key={opt.key} to={showAllUrl(opt.kind)} id={optionId(i)} role="option" aria-selected={activeIndex === i} tabIndex={-1}
            onMouseMove={() => setActiveIndex(i)} onClick={() => { setRecent(saveRecentSearch(query)); setOpen(false); if (onNavigate) onNavigate(); }}
            style={{ ...optionStyle(i), display: 'flex', alignItems: 'center', gap: space.xs, minHeight: 40, padding: `0 ${space.md}px`, fontSize: font.small, fontWeight: weight.medium, color: color.primary, textDecoration: 'none' }}>
            הצגת כל {opt.count.toLocaleString('he-IL')} התוצאות<Icon name="chevronForward" size={14} />
          </Link>
        );
      }
      return (
        <div key={opt.key} id={optionId(i)} role="option" aria-selected={activeIndex === i}
          onMouseMove={() => setActiveIndex(i)} onMouseDown={e => e.preventDefault()} onClick={() => activate(opt)}
          style={{ ...optionStyle(i), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: space.sm, minHeight: 40, padding: `0 ${space.md}px`, fontSize: font.small }}>
          <span style={{ color: color.textTertiary }}><Icon name="clock" size={16} /></span>{opt.text}
        </div>
      );
    };
    return (
      <div ref={rootRef} style={{ position: 'relative', width: '100%' }}>
        {field}
        {showPanel && (
          <div style={{
            position: 'absolute', top: `calc(100% + ${space.xs + 2}px)`, right: 0, width: panelWidth, maxHeight: panelMaxHeight,
            overflowY: 'auto', zIndex: 300, background: color.surface, borderRadius: radius.lg, boxShadow: shadow.floating,
            padding: `${space.xs}px 0`, boxSizing: 'border-box', textAlign: 'right',
          }}>
            <div id={listId} role="listbox" aria-label="תוצאות חיפוש">
              {!trimmed && recent.length > 0 && (
                <div role="group" aria-label="חיפושים אחרונים">
                  <div style={{ padding: `${space.xs + 2}px ${space.md}px`, fontSize: font.caption, color: color.textSecondary }}>חיפושים אחרונים</div>
                  {options.filter(o => o.type === 'recent').map(renderOption)}
                </div>
              )}
              {tooShort && <div style={{ padding: space.md, fontSize: font.small, color: color.textSecondary }}>הקלידו לפחות שתי אותיות</div>}
              {trimmed.length >= MIN_QUERY_LENGTH && groups.map(g => (
                <div key={g.kind} role="group" aria-label={SEARCH_KIND_LABEL[g.kind]}>
                  <div style={{ padding: `${space.sm}px ${space.md}px ${space.xs}px`, fontSize: font.caption, fontWeight: weight.bold, color: color.textSecondary }}>
                    {SEARCH_KIND_LABEL[g.kind]} · {g.hits.length.toLocaleString('he-IL')}
                  </div>
                  {options.filter(o => (o.type === 'hit' && o.hit.kind === g.kind) || (o.type === 'more' && o.kind === g.kind)).map(renderOption)}
                </div>
              ))}
              {trimmed.length >= MIN_QUERY_LENGTH && loading && groups.length === 0 && <div style={{ padding: space.md, fontSize: font.small, color: color.textSecondary }} role="status">מחפש…</div>}
              {trimmed.length >= MIN_QUERY_LENGTH && !loading && !error && total === 0 && <div style={{ padding: space.md, fontSize: font.small, color: color.textSecondary }}>לא נמצאו תוצאות</div>}
              {error && <div style={{ padding: space.md, fontSize: font.small, color: color.textSecondary }} role="alert">חלק מהנתונים לא נטענו. נסו שוב מאוחר יותר.</div>}
              {billsPartial && trimmed.length >= MIN_QUERY_LENGTH && <div style={{ padding: `${space.xs}px ${space.md}px`, fontSize: font.caption, color: color.textTertiary }}>מחפש גם בשאר הצעות החוק…</div>}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── Page ── */
  const header = (
    <div style={{ position: 'sticky', top: 0, zIndex: 50, background: color.page, display: 'flex', alignItems: 'center', gap: space.xs, padding: `${space.sm}px 0` }}>
      {leading}
      <div style={{ flex: 1, minWidth: 0 }}>{field}</div>
    </div>
  );

  let body: React.ReactNode;
  if (!trimmed) {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {recent.length > 0 && (
          <Section
            title="חיפושים אחרונים"
            action={<Button variant="ghost" onClick={() => { clearRecentSearches(); setRecent([]); }} style={{ padding: `0 ${space.sm}px` }}>ניקוי</Button>}
          >
            <Card padding={0}>
              {recent.map((r, i) => (
                <button key={r} type="button" onClick={() => { setQuery(r); inputRef.current?.focus(); }}
                  style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: TAP + 4, padding: `0 ${space.lg}px`, display: 'flex', alignItems: 'center', gap: space.md, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none', fontSize: font.label }}>
                  <span style={{ color: color.textTertiary }}><Icon name="clock" size={18} /></span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r}</span>
                </button>
              ))}
            </Card>
          </Section>
        )}
        <Card>
          <div style={{ fontSize: font.label, color: color.textSecondary, lineHeight: 1.6 }}>
            אפשר לחפש חבר/ת כנסת, סיעה, ועדה, משרד ממשלתי, הצעת חוק, שדלן, לקוח של שדלנים או שאילתה.
          </div>
          <ChipRow style={{ flexWrap: 'wrap', marginTop: space.sm }}>
            {EXAMPLES.map(x => <Chip key={x} variant="flat" onClick={() => setQuery(x)}>{x}</Chip>)}
          </ChipRow>
        </Card>
      </div>
    );
  } else if (tooShort) {
    body = <Card padding={0}><EmptyState icon="search" title="הקלידו לפחות שתי אותיות" /></Card>;
  } else if (error && groups.length === 0) {
    body = <Card padding={0}><ErrorState onRetry={() => window.location.reload()} /></Card>;
  } else if (groups.length === 0) {
    body = loading
      ? <SkeletonList rows={6} avatar />
      : <Card padding={0}><EmptyState icon="search" title={`לא נמצאו תוצאות ל״${trimmed}״`} body="נסו מילה אחרת, או רק חלק מהשם." /></Card>;
  } else {
    const single: SearchGroup | undefined = type ? groups.find(g => g.kind === type) : undefined;
    const groupSection = (g: SearchGroup, limit: number, all: boolean) => (
      <Section
        key={g.kind}
        title={SEARCH_KIND_LABEL[g.kind]}
        meta={`${g.hits.length.toLocaleString('he-IL')}${g.kind === 'bill' && billsPartial ? ' · מחפש בעוד הצעות…' : ''}`}
        action={all
          ? <Button variant="ghost" onClick={() => setType(null)} style={{ padding: `0 ${space.sm}px` }}>כל התוצאות</Button>
          : g.hits.length > limit
            ? <Button variant="ghost" onClick={() => setType(g.kind)} style={{ padding: `0 ${space.sm}px` }}>הצגת הכל</Button>
            : undefined}
      >
        <Card padding={0}>
          {g.hits.slice(0, limit).map((hit, i) => <HitRow key={hit.id} hit={hit} query={query} compact={false} divider={i > 0} onPick={pick} />)}
        </Card>
      </Section>
    );
    body = single
      ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
          {groupSection(single, visible, true)}
          {single.hits.length > visible && (
            <Button fullWidth onClick={() => setVisible(visible + PAGE_SIZE)}>הצגת עוד ({(single.hits.length - visible).toLocaleString('he-IL')} נוספות)</Button>
          )}
        </div>
      )
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
          <div role="status" style={{ fontSize: font.small, color: color.textSecondary }}>{total.toLocaleString('he-IL')} תוצאות</div>
          {groups.map(g => groupSection(g, PAGE_PREVIEW, false))}
        </div>
      );
  }

  return (
    <div style={{ width: '100%' }}>
      {header}
      <div style={{ paddingTop: space.sm }}>{body}</div>
    </div>
  );
};

export default GlobalSearch;
